import React, { useState, useEffect } from 'react';
import { Bell, X, Check, Filter, MessageSquare, Package, AlertCircle, CheckCircle, ArrowRight, ExternalLink } from 'lucide-react';
import { notificationService } from '../services/notificationService';
import { unreadCounterService } from '../services/unreadCounterService';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { sr } from 'date-fns/locale';
import realtimeService from '../services/realtimeService';

const NotificationCenter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [tradingAlerts, setTradingAlerts] = useState([]);

  const notificationTypes = [
    { value: 'all', label: 'Sve', icon: Bell },
    { value: 'message', label: 'Poruke', icon: MessageSquare },
    { value: 'negotiation', label: 'Pregovori', icon: Package },
    { value: 'dispute', label: 'Sporovi', icon: AlertCircle },
    { value: 'notification', label: 'Obavještenja', icon: CheckCircle }
  ];

  useEffect(() => {
    if (user?.id) {
      loadNotifications();

      // Subscribe to real-time updates
      const unsubscribe = unreadCounterService?.subscribeToUnreadUpdates(
        user?.id,
        () => loadNotifications()
      );

      // Subscribe to real-time notification updates
      const notificationChannel = notificationService?.subscribeToNotifications(
        user?.id,
        (payload) => {
          console.log('Real-time notification:', payload);
          loadNotifications();
          loadUnreadCount();
        }
      );

      // Subscribe to trading alerts for instant notifications
      const tradingAlertsSubscription = realtimeService?.subscribeToTradingAlerts(
        user?.id,
        (alert) => {
          console.log('Real-time trading alert:', alert);
          
          // Add to trading alerts state for display
          setTradingAlerts(prev => [alert, ...prev]?.slice(0, 5));
          
          // Show browser notification if permission granted
          if (Notification?.permission === 'granted') {
            new Notification(alert?.message, {
              body: getTradingAlertBody(alert),
              icon: '/favicon.ico',
              tag: `trading-alert-${alert?.type}-${Date.now()}`
            });
          }
          
          // Reload notifications to show in the list
          loadNotifications();
          loadUnreadCount();
        }
      );

      // Request notification permission
      if (Notification?.permission === 'default') {
        Notification?.requestPermission();
      }

      return () => {
        if (unsubscribe) unsubscribe();
        if (notificationChannel) {
          notificationService?.unsubscribeFromNotifications(notificationChannel);
        }
        if (tradingAlertsSubscription) {
          tradingAlertsSubscription?.unsubscribe();
        }
      };
    }
  }, [user?.id, selectedFilter]);

  const loadNotifications = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const data = await unreadCounterService?.getConsolidatedNotifications(user?.id);
      
      // Filter by type if selected
      let filteredData = data;
      if (selectedFilter !== 'all') {
        filteredData = data?.filter(n => n?.type === selectedFilter);
      }
      
      setNotifications(filteredData || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add this function to load unread count
  const loadUnreadCount = async () => {
    if (!user?.id) return;
    
    try {
      const data = await unreadCounterService?.getConsolidatedNotifications(user?.id);
      setUnreadCount(data?.length || 0);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const handleMarkAsRead = async (notification) => {
    try {
      await unreadCounterService?.markAsHandled(notification?.id, notification?.type);
      setNotifications(prev => prev?.filter(n => n?.id !== notification?.id));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleQuickAction = (notification) => {
    handleMarkAsRead(notification);
    setIsOpen(false);
    navigate(notification?.actionPath);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-50 border-red-200';
      case 'high':
        return 'bg-orange-50 border-orange-200';
      case 'medium':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      urgent: { text: 'Hitno', color: 'bg-red-100 text-red-800' },
      high: { text: 'Visok prioritet', color: 'bg-orange-100 text-orange-800' },
      medium: { text: 'Srednji prioritet', color: 'bg-blue-100 text-blue-800' },
      low: { text: 'Nizak prioritet', color: 'bg-gray-100 text-gray-800' }
    };
    
    const badge = badges?.[priority] || badges?.low;
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${badge?.color}`}>
        {badge?.text}
      </span>
    );
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message':
        return <MessageSquare size={20} className="text-blue-600" />;
      case 'negotiation':
        return <Package size={20} className="text-purple-600" />;
      case 'dispute':
        return <AlertCircle size={20} className="text-red-600" />;
      case 'notification':
        return <CheckCircle size={20} className="text-green-600" />;
      default:
        return <Bell size={20} className="text-gray-600" />;
    }
  };

  const getTradingAlertBody = (alert) => {
    switch (alert?.type) {
      case 'negotiation':
        return alert?.event === 'new' ?'Novi pregovor je pokrenut' :'Status pregovora je promenjen';
      case 'transaction':
        return alert?.event === 'new' ?'Nova transakcija je kreirana' :'Transakcija je ažurirana';
      case 'wish':
        return alert?.event === 'new' ?'Nova želja je objavljena' :'Želja je ažurirana';
      default:
        return alert?.message;
    }
  };

  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <Bell size={24} />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <>
          {/* Overlay to close dropdown */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          <div className="absolute right-0 mt-2 w-[480px] bg-white rounded-lg shadow-xl border border-gray-200 z-20 max-h-[600px] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Centar za obavještenja
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Sve nepročitane poruke i obavještenja
                  </p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Filter Tabs */}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {notificationTypes?.map((type) => {
                  const IconComponent = type?.icon;
                  const count = selectedFilter === 'all' 
                    ? notifications?.length 
                    : notifications?.filter(n => n?.type === type?.value)?.length;
                  
                  return (
                    <button
                      key={type?.value}
                      onClick={() => setSelectedFilter(type?.value)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                        selectedFilter === type?.value
                          ? 'bg-blue-100 text-blue-700' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <IconComponent size={14} />
                      {type?.label}
                      {count > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 bg-white rounded-full text-xs">
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Notification List */}
            <div className="overflow-y-auto flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
                </div>
              ) : notifications?.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="mx-auto text-gray-300 mb-4" size={48} />
                  <p className="text-gray-500 font-medium">Nemate novih obavještenja</p>
                  <p className="text-gray-400 text-sm mt-1">Sve je spremno!</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifications?.map((notification) => (
                    <div
                      key={notification?.id}
                      className={`p-4 hover:bg-gray-50 transition-colors border-l-4 ${getPriorityColor(notification?.priority)}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          {getNotificationIcon(notification?.type)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="text-sm font-semibold text-gray-900">
                              {notification?.title}
                            </p>
                            {getPriorityBadge(notification?.priority)}
                          </div>
                          
                          <p className="text-sm text-gray-600 mb-2">
                            {notification?.message}
                          </p>
                          
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-gray-400">
                              {formatDistanceToNow(new Date(notification?.createdAt), {
                                addSuffix: true,
                                locale: sr
                              })}
                            </p>
                            
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleMarkAsRead(notification)}
                                className="p-1 text-blue-600 hover:text-blue-800 rounded"
                                title="Označi kao pročitano"
                              >
                                <Check size={16} />
                              </button>
                              
                              <button
                                onClick={() => handleQuickAction(notification)}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
                              >
                                {notification?.actionLabel}
                                <ArrowRight size={12} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications?.length > 0 && (
              <div className="p-3 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/zajednica');
                  }}
                  className="w-full text-center text-sm font-medium text-blue-700 hover:text-blue-900 flex items-center justify-center gap-2"
                >
                  Vidi sve u Komunikacijama
                  <ExternalLink size={14} />
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationCenter;